import { cn } from '@/app/utils/style'
import CrossIcon from '@/public/images_pwf/comparison_cross.svg'
import TickIcon from '@/public/images_pwf/comparison_tick.svg'
import type { ComparisonTable as ComparisonTableType } from '@/sanity.types'

type ComparisonTableProps = {
  value: ComparisonTableType
}

export function ComparisonTable({ value }: ComparisonTableProps) {
  const { conHeading, proHeading, rows } = value

  if (!rows || rows.length === 0) {
    return null
  }

  return (
    <div className="my-6xl">
      <div className="overflow-x-auto pr-sm">
        <div className="min-w-0 w-fit border border-neutral-50 rounded-md overflow-hidden">
          {/* Header Row */}
          <div className="grid grid-cols-[94px_minmax(120px,1fr)_minmax(120px,1fr)] md:grid-cols-[minmax(94px,188px)_1fr_1fr]">
            {/* Empty cell for feature column */}
            <div className="bg-neutral-50" />

            {/* Con Heading */}
            <div className="bg-neutral-50 p-sm md:p-md lg:py-2xl lg:px-lg flex items-center">
              <p className="size-body-xs text-white-950 font-normal">{conHeading}</p>
            </div>

            {/* Pro Heading */}
            <div className="bg-green-500 p-sm md:p-md lg:py-2xl lg:px-lg flex items-center">
              <p className="size-body-xs text-white-default font-normal">{proHeading}</p>
            </div>
          </div>

          {/* Data Rows */}
          {rows.map((row, index) => {
            if (!row || !row.feature) return null

            return (
              <div
                key={index}
                className={cn(
                  'grid grid-cols-[94px_minmax(120px,1fr)_minmax(120px,1fr)] md:grid-cols-[minmax(94px,188px)_1fr_1fr] border-t border-neutral-50',
                  index === rows.length - 1 && 'rounded-b-md overflow-hidden',
                )}
              >
                <div className="bg-neutral-50 p-sm md:p-md lg:py-2xl lg:px-lg flex items-center">
                  <p className="size-body-xs text-white-950 font-normal break-words">
                    {row.feature}
                  </p>
                </div>
                <div className="p-sm md:p-md lg:py-2xl lg:px-lg flex items-start gap-0">
                  {row.con && (
                    <>
                      <div className="flex items-start justify-start size-[20px] shrink-0">
                        <CrossIcon className="w-[10px] h-auto text-coral-300 relative top-[6px]" />
                      </div>
                      <p className="size-body-xs text-white-600 font-normal flex-1 break-words">
                        {row.con}
                      </p>
                    </>
                  )}
                </div>
                <div className="bg-green-50 p-sm md:p-md lg:py-2xl lg:px-lg flex items-start gap-0">
                  {row.pro && (
                    <>
                      <div className="flex items-start justify-start size-[20px] shrink-0">
                        <TickIcon className="w-[11px] h-auto text-green-200 relative top-[6px]" />
                      </div>
                      <p className="size-body-xs text-white-900 font-normal flex-1 break-words">
                        {row.pro}
                      </p>
                    </>
                  )}
                </div>
              </div>
            )
          })}
        </div>
      </div>
    </div>
  )
}
